import "@/App.css";
import "@/index.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

// User Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import DashboardPage from "@/pages/DashboardPage";
import InvoicesPage from "@/pages/InvoicesPage";
import InvoiceBuilderPage from "@/pages/InvoiceBuilderPage";
import CustomersPage from "@/pages/CustomersPage";
import VendorsPage from "@/pages/VendorsPage";
import ExpensesPage from "@/pages/ExpensesPage";
import SettingsPage from "@/pages/SettingsPage";
import MigrationAssessmentPage from "@/pages/MigrationAssessmentPage";
import MigrationWizardPage from "@/pages/MigrationWizardPage";

// CA Portal Pages
import CALoginPage from "@/pages/ca/CALoginPage";
import CASignupPage from "@/pages/ca/CASignupPage";
import CADashboardPage from "@/pages/ca/CADashboardPage";
import CAClientsPage from "@/pages/ca/CAClientsPage";
import CAClientDetailPage from "@/pages/ca/CAClientDetailPage";
import CACompliancePage from "@/pages/ca/CACompliancePage";
import CARevenuePage from "@/pages/ca/CARevenuePage";

// Admin Portal Pages
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminCAsPage from "@/pages/admin/AdminCAsPage";
import AdminWaitlistPage from "@/pages/admin/AdminWaitlistPage";
import AdminFeedbackPage from "@/pages/admin/AdminFeedbackPage";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#1D9E75] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="w-12 h-12 border-4 border-[#1D9E75] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const LIGHT_ROUTES = ["/signup", "/dashboard", "/invoices", "/customers", "/vendors", "/expenses", "/settings", "/migrate"];

// Business-user pages use the light theme; landing, CA and admin portals stay dark
const ThemeSync = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const light = LIGHT_ROUTES.some((r) => pathname === r || pathname.startsWith(`${r}/`));
    document.documentElement.classList.toggle("light", light);
  }, [pathname]);
  return null;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
      <Route path="/migration-assessment" element={<MigrationAssessmentPage />} />
      
      {/* User Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
      <Route path="/invoices/new" element={<ProtectedRoute><InvoiceBuilderPage /></ProtectedRoute>} />
      <Route path="/invoices/:id" element={<ProtectedRoute><InvoiceBuilderPage /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
      <Route path="/vendors" element={<ProtectedRoute><VendorsPage /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/migrate" element={<ProtectedRoute><MigrationWizardPage /></ProtectedRoute>} />
      
      {/* CA Portal routes */}
      <Route path="/ca/login" element={<CALoginPage />} />
      <Route path="/ca/signup" element={<CASignupPage />} />
      <Route path="/ca/dashboard" element={<CADashboardPage />} />
      <Route path="/ca/clients" element={<CAClientsPage />} />
      <Route path="/ca/clients/:id" element={<CAClientDetailPage />} />
      <Route path="/ca/compliance" element={<CACompliancePage />} />
      <Route path="/ca/revenue" element={<CARevenuePage />} />
      
      {/* Admin Portal routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
      <Route path="/admin/users" element={<AdminUsersPage />} />
      <Route path="/admin/cas" element={<AdminCAsPage />} />
      <Route path="/admin/waitlist" element={<AdminWaitlistPage />} />
      <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ThemeSync />
        <AppRoutes />
        <Toaster position="bottom-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
